import * as ko from 'knockout';

import * as internalApi from '../../api/internal';
import { ChartList } from '../../ko_bindings/bar_chart';
import { ALL_PLANS } from './tenant_subscription_edit';
import { Grid, h, html } from 'gridjs';
import { TColumn } from 'gridjs/dist/src/types';
import { tryFormatDate } from '../../utils';
import { FilterDelegate } from '../../components/list_filters';
import 'gridjs/dist/theme/mermaid.css';

let template = require('raw-loader!../../../templates/internal/tenants_usage.html').default;

const DISPLAY_CHART_BUTTON_MESSAGE = {
  loading: 'Loading Chart...',
  display: 'Display Chart',
  hide: 'Hide Chart',
  unavailable: 'Chart Not Available',
};

const SUBSCRIPTION_TABLE_COLUMNS: TColumn[] = [
  { id: 'id', name: 'id', hidden: true },
  {
    id: 'name',
    name: 'Customer',
    sort: { enabled: true },
    attributes: (column: TColumn) => {
      if (column) {
        return { class: 'gridjs-td left-align-column' };
      }
    },
    formatter: (cell, row) => html(`<a href="/tenants-usage/edit/${row.cells[0].data}/">${cell}</a>`),
  },
  {
    id: 'subscription_plan',
    name: 'Plan',
    sort: { enabled: true },
    width: '105px',
    formatter: (cell: string) => (cell ? ALL_PLANS.find((p) => p.id === cell).name : 'Free Trial'),
  },
  {
    id: 'expiration_date',
    name: 'Expiration',
    width: '155px',
    sort: { enabled: true },
    formatter: (cell: string) => tryFormatDate(cell),
  },
  {
    id: 'non_discounted_monthly_price',
    name: 'Monthly $',
    sort: { enabled: true },
    width: '110px',
    attributes: (column: TColumn) => {
      if (column !== null) {
        return { class: 'gridjs-td right-align-column' };
      }
    },
    formatter: (cell) => parseFloat(cell?.toString())?.toLocaleString(),
  },
  {
    id: 'discount',
    name: 'Discount %',
    sort: { enabled: true },
    width: '115px',
    attributes: (column: TColumn) => {
      if (column !== null) {
        return { class: 'gridjs-td right-align-column' };
      }
    },
    formatter: (cell) => parseFloat(cell?.toString())?.toLocaleString(),
  },
  {
    id: 'subscription_add_on_price',
    name: 'Addons $',
    sort: { enabled: true },
    width: '105px',
    attributes: (column: TColumn) => {
      if (column !== null) {
        return { class: 'gridjs-td right-align-column' };
      }
    },
    formatter: (cell) => (cell ? parseFloat(cell?.toString()).toLocaleString() : '-'),
  },
  {
    id: 'annual_price',
    name: 'Total annual $',
    sort: { enabled: true },
    width: '135px',
    attributes: (column: TColumn) => {
      if (column !== null) {
        return { class: 'gridjs-td right-align-column' };
      }
    },
    formatter: (cell) => parseFloat(cell?.toString()).toLocaleString(),
  },
  {
    id: 'automatic_payment',
    name: 'Stripe',
    sort: { enabled: true },
    width: '105px',
    formatter: (cell) => (cell ? 'Yes' : 'No'),
  },
  { id: 'subscription_comment', name: 'Comment' },
];

const DISPLAY_SUBSCRIPTION_OPTIONS = [
  {
    id: 'ACTIVE',
    value: 'active',
  },
  {
    id: 'TRIAL',
    value: 'trial',
  },
  {
    id: 'ALL',
    value: 'all',
  },
  {
    id: 'EXPIRED',
    value: 'expired',
  },
];
type ListageOptions = 'all' | 'active' | 'trial' | 'expired';

class TenantsUsageScreen {
  subs: internalApi.TenantSubscriptionData[];
  charts = ko.observable<ChartList>({});
  loadingCharts = ko.observable<Record<string, boolean>>({});
  loading = ko.observable({
    subscriptions: true,
  });
  chartsDisplayed = ko.observable(0);
  currentView = ko.observable<ListageOptions>('active');
  viewOptions = DISPLAY_SUBSCRIPTION_OPTIONS;

  private tenantFeatureSelectedEntities = ko.observableArray();

  tenantFeatureFilter: FilterDelegate[] = [
    {
      title: 'Enabled Tenant Features',
      entities: this.tenantFeatureSelectedEntities,
      list: (_) => {
        return Promise.resolve([
          { id: 'observation_history_enabled', name: 'Observation History tab' },
          { id: 'observation_group_tab_enabled', name: 'Observation Group tab' },
          { id: 'barcode_scan_action_enabled', name: 'Barcode scan action' },
          { id: 'manual_trials_enabled', name: 'Manual Trials' },
          { id: 'trials_advanced_search_toggle_enabled', name: 'Advanced trial search' },
          { id: 'map_visualization_enabled', name: 'Map Visualization' },
          { id: 'data_warehouse_access_enabled', name: 'Data Warehouse Access' },
          { id: 'api_integration_enabled', name: 'API Integration' },
          { id: 'treatment_management_enabled', name: 'Treatment Management' },
          { id: 'custom_reports_enabled', name: 'Custom Reports' },
          { id: 'limit_traits_to_dimensions_enabled', name: 'Limit traits to dimensions' },
          { id: 'mobile_derived_traits_enabled', name: 'Mobile derived traits' },
          { id: 'ai_enabled', name: 'Q AI' },
          { id: 'glp', name: 'GLP mode' },
        ]);
      },
    },
  ];

  constructor() {
    this.currentView.subscribe(this.toggleView);
    this.tenantFeatureSelectedEntities.subscribe(this.onFiltersChange);
    const subsPromise = internalApi.tenantsSubscriptions('active');
    subsPromise.then((value) => {
      this.subs = value;
      this.loading({ ...this.loading(), subscriptions: false });
      this.renderUsageTable();
    });
  }

  onFiltersChange = () => {
    this.loading({ ...this.loading(), subscriptions: true });

    const selectedIds = this.tenantFeatureSelectedEntities().map((entity) => entity.id);

    let subs = internalApi.tenantsSubscriptions(this.currentView(), {
      observation_history_enabled: selectedIds.includes('observation_history_enabled'),
      observation_group_tab_enabled: selectedIds.includes('observation_group_tab_enabled'),
      barcode_scan_action_enabled: selectedIds.includes('barcode_scan_action_enabled'),
      manual_trials_enabled: selectedIds.includes('manual_trials_enabled'),
      map_visualization_enabled: selectedIds.includes('map_visualization_enabled'),
      data_warehouse_access_enabled: selectedIds.includes('data_warehouse_access_enabled'),
      api_integration_enabled: selectedIds.includes('api_integration_enabled'),
      treatment_management_enabled: selectedIds.includes('treatment_management_enabled'),
      custom_reports_enabled: selectedIds.includes('custom_reports_enabled'),
      limit_traits_to_dimensions_enabled: selectedIds.includes('limit_traits_to_dimensions_enabled'),
      mobile_derived_traits_enabled: selectedIds.includes('mobile_derived_traits_enabled'),
      ai_enabled: selectedIds.includes('ai_enabled'),
      glp: selectedIds.includes('glp'),
    });
    subs.then((data) => {
      this.subs = data;
      this.loading({ ...this.loading(), subscriptions: false });
      this.renderUsageTable();
    });
  };

  renderUsageTable = () => {
    const DISPLAY_CHART_COLUMNS: TColumn = {
      id: 'display_chart_button',
      name: 'Chart',
      sort: { enabled: true },
      width: '105px',
      attributes: (column: TColumn) => {
        if (column !== null) {
          return { class: 'gridjs-td right-align-column' };
        }
      },
      formatter: (cell, row) => {
        const tenantName = row.cells[1].data as string;
        const tenantId = row.cells[0].data as string;
        const initialMessage = this.getMessage(tenantName);

        return h(
          'button',
          {
            onClick: (event: any) => this.toggleChart(event, tenantName, tenantId),
            className: 'display-chart-button',
            'data-tenantname': tenantName,
            'data-tenantid': tenantId,
          },
          initialMessage
        );
      },
    };
    const grid = new Grid({
      columns: [...SUBSCRIPTION_TABLE_COLUMNS, DISPLAY_CHART_COLUMNS],
      data: this.subs as {}[],
      search: true,
      style: {
        td: {
          'text-align': 'center',
        },
      },
      language: {
        search: {
          placeholder: '🔍 Search...',
        },
      },
    });

    grid.render(document.getElementById('tenants-subscription'));
  };
  toggleView = () => {
    this.loading({ ...this.loading(), subscriptions: true });

    let subs = internalApi.tenantsSubscriptions(this.currentView());
    subs.then((data) => {
      this.subs = data;
      this.loading({ ...this.loading(), subscriptions: false });
      this.renderUsageTable();
    });
  };

  getMessage = (tenantName: string) => {
    if (this.loadingCharts()[tenantName]) {
      return DISPLAY_CHART_BUTTON_MESSAGE.loading;
    }
    if (tenantName in this.charts()) {
      return this.charts()[tenantName].visible
        ? DISPLAY_CHART_BUTTON_MESSAGE.hide
        : DISPLAY_CHART_BUTTON_MESSAGE.display;
    }
    return DISPLAY_CHART_BUTTON_MESSAGE.display;
  };

  toggleChart = async (event: MouseEvent, tenantName: string, tenantId: string) => {
    const button = event.target as HTMLButtonElement;

    // If chart data doesn't exist yet, fetch it
    if (!(tenantName in this.charts())) {
      // Set loading state
      this.loadingCharts({ ...this.loadingCharts(), [tenantName]: true });
      button.textContent = DISPLAY_CHART_BUTTON_MESSAGE.loading;
      button.disabled = true;

      try {
        const tenantChartData = await internalApi.tenantUsage(tenantId);

        const chartsHolder = this.charts();
        chartsHolder[tenantName] = {
          type: 'bar' as 'bar',
          scaleType: 'time' as 'time',
          title: tenantName,
          yTitle: 'N. of facts',
          xTitle: '',
          labels: tenantChartData.labels,
          fontSize: 14,
          timeUnit: 'month' as 'month',
          datasets: [
            {
              label: '',
              data: tenantChartData.values,
            },
          ],
          visible: true,
        };

        this.charts(chartsHolder);
        this.chartsDisplayed(this.chartsDisplayed() + 1);

        // Clear loading state
        this.loadingCharts({ ...this.loadingCharts(), [tenantName]: false });
        button.textContent = DISPLAY_CHART_BUTTON_MESSAGE.hide;
        button.disabled = false;
      } catch (error) {
        console.error('Failed to load chart data:', error);
        this.loadingCharts({ ...this.loadingCharts(), [tenantName]: false });
        button.textContent = DISPLAY_CHART_BUTTON_MESSAGE.unavailable;
        button.disabled = true;
      }
    } else {
      // Chart data exists, just toggle visibility
      button.textContent =
        button.textContent === DISPLAY_CHART_BUTTON_MESSAGE.display
          ? DISPLAY_CHART_BUTTON_MESSAGE.hide
          : DISPLAY_CHART_BUTTON_MESSAGE.display;

      const chartsHolder = this.charts();
      const shouldDisplay = !this.charts()[tenantName].visible;

      // Need to create a new object instead of simply changing chartsHolder[tenantName].visible
      // otherwise knockout will not compute the change and update the UI
      chartsHolder[tenantName] = {
        ...chartsHolder[tenantName],
        visible: shouldDisplay,
      };

      this.charts(chartsHolder);
      this.chartsDisplayed(this.chartsDisplayed() + (shouldDisplay ? 1 : -1));
    }
  };
}

export let tenantsUsage = {
  name: 'tenants-usage',
  viewModel: TenantsUsageScreen,
  template: template,
};

ko.components.register(tenantsUsage.name, tenantsUsage);
